import {
    isFeatureEnabled,
    toFeatureState,
} from '@west-australian-newspapers/etrigan-feature-toggles-client'
import { SectionMetaInfoDTO } from '@west-australian-newspapers/publication-types'
import pino from 'pino'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { debugSwg } from '../../authentication'
import { getLoginUrl } from '../../authentication/helpers'
import { isArticle } from '../../client-data-types'
import { BreachContext } from '../../context-providers/BreachContextProvider'
import { ConfigurationContext } from '../../context-providers/configuration-context'
import { getPageUrl } from '../../helpers/getPageUrl'
import { AppState } from '../../store'
import { registerUser } from './extended-access'
import {
    buildRedirectUrl,
    sendSwGNewStartMessage,
    SwgMetaInfo,
} from './subscriptions'
import { UserState } from './swg-api'

export const useSubscribeWithGoogle = (
    buttonRef: HTMLButtonElement,
    googleSKU: string,
    sectionMetaInfo: SectionMetaInfoDTO,
    breachContext: BreachContext | undefined,
    swgMetaInfo?: SwgMetaInfo,
) => {
    /** READ THIS BEFORE MODIFIYING THE CODE BELOW:
     *  If you modify the code below, make sure to check the following three places while testing:
     *  - Standard Breach Screens (SwG Button) (SubscriptionPackage.tsx)
     *  - Black Friday Breach Screens (SwG Button) (BlackFridaySubsriptionPackage.tsx)
     *  - RegionalsSwgPromoPage (SwG Button) (RegionalsSwGPromoPage.tsx)
     *  Make sure you also lookout for any extra renders if you modify any of the useEffect dependencies below.
     */

    const { subscriptions, eventManagerLoaded } = useSelector(
        (state: AppState) => state.SwG,
    )

    const toggles = useSelector((state: AppState) => state.toggles)
    const useImprovedSwgButtonHandlers = isFeatureEnabled(
        toFeatureState(toggles),
        'breach-show-digital-radio-and-papers',
    )
    const {
        userEmail,
        checkingTokenState,
        hasAttemptedValidation,
        userName,
        wanUserId,
    } = useSelector((state: AppState) => state.authentication)

    const localConfig = useContext(ConfigurationContext)
    const dispatch = useDispatch()

    const location = useLocation()

    const breachInitialised = Boolean(breachContext)

    // Ref to store the last attached handler & SKU, this is required to prevent multiple handlers being attached to the same button with different SKUs
    const lastHandlerRef = useRef<(() => void) | null>(null)
    const subscriptionWindowActiveRef = useRef<boolean>(false)
    const currentSkuRef = useRef<string | undefined>(undefined)

    const getCteNextUrl = useCallback(
        (email: string, userInfo: UserState | null, hint?: string) => {
            // If no user info, cannot be logged in via CTE
            // return null to stay on the same page
            if (!userInfo || !userInfo.id) {
                return null
            }
            const desiredRedirect: URL | Location = swgMetaInfo?.desiredRedirect
                ? new URL(swgMetaInfo?.desiredRedirect, window.location.href)
                : window.location

            // Need to see if the user is logged in to the account they bought the SKU with
            const isSameAccount = email === userInfo?.email
            const redirectUrl =
                isSameAccount && userInfo?.granted
                    ? buildRedirectUrl(desiredRedirect)
                    : getLoginUrl(
                          {
                              prompt: 'google',
                              login_hint: hint,
                          },
                          undefined,
                          desiredRedirect.href,
                      )

            debugSwg(redirectUrl)

            return redirectUrl
        },
        [swgMetaInfo],
    )

    // Attempt RRME Google Auto Login, assuming not-logged in state
    const attemptRrmeGoogleAutoLogin = useCallback(
        async (options: {
            response: any
            sku: string
        }): Promise<UserState | null> => {
            const logger = pino()
            const { response, sku } = options
            try {
                debugSwg(
                    'RRME enabled: attempting inline token exchange (auto-login)',
                    { sku, wanUserId },
                )
                // Register a user with the userData idToken
                const userState = await registerUser(
                    response.userData.idToken,
                    dispatch,
                    logger,
                )
                debugSwg(
                    { sku, wanUserId },
                    'RRME enabled: inline token exchange completed',
                )
                return userState
            } catch (err) {
                debugSwg(
                    'RRME enabled: inline token exchange failed, falling back to legacy redirect',
                    err,
                )
                logger?.error('RRME token exchange failed')
                return null
            }
        },
        [dispatch, wanUserId],
    )

    const handleSubscribeWithGoogleButton = useCallback(
        (sku?: string, swgMetaInfo?: SwgMetaInfo) => {
            debugSwg(
                `handleSubscribeWithGoogleButton: desiredRedirect:${swgMetaInfo?.desiredRedirect}`,
            )

            if (!subscriptions) {
                debugSwg('subscriptions not available!')
                return
            }

            if (!sku) {
                debugSwg('sku not available!')
                return
            }

            // Only proceed if the SKU matches the latest one
            if (useImprovedSwgButtonHandlers && sku !== currentSkuRef.current) {
                debugSwg(
                    `Ignoring click: handler SKU (${sku}) does not match current SKU (${currentSkuRef.current})`,
                )
                return
            }

            debugSwg(breachContext)

            if (subscriptionWindowActiveRef.current) {
                debugSwg('Google Payment already initialised; skipping')
                return
            }
            subscriptionWindowActiveRef.current = true

            subscriptions.setOnFlowCanceled(() => {
                subscriptionWindowActiveRef.current = false
                debugSwg('payment flow cancelled')
            })

            // Setup callback for payment response, noting that this callback only gets triggered when the user successfully subscribes to the SKU
            subscriptions.setOnPaymentResponse((paymentResponse) => {
                debugSwg('payment promise received')
                paymentResponse
                    .then(async (response) => {
                        debugSwg('payment promise resolved')
                        // At this point, we need to handle the userData as well as purchaseData from the user
                        // We first need to publish the SKU purchase message to the analytics firehose
                        const article =
                            breachContext?.publication &&
                            isArticle(breachContext?.publication)
                                ? breachContext?.publication
                                : undefined

                        // We don't want to send a message if we have no context for it.
                        if (breachContext && article) {
                            const pageUrl = getPageUrl(
                                sectionMetaInfo,
                                localConfig.publicUrl,
                                location.pathname,
                            )

                            const parsedPurchaseData = JSON.parse(
                                response.purchaseData.data,
                            )
                            const orderId = parsedPurchaseData.orderId

                            // not a WAN value, just Google's unique user ID
                            const googleId = response.userData.id

                            try {
                                // This SwGNewStartMessage is for the subtastic analytics
                                debugSwg('Sending newstart message...')
                                await sendSwGNewStartMessage({
                                    wanUserId: wanUserId,
                                    pageUrl: pageUrl,
                                    purchaseToken: response.purchaseData.data,
                                    orderId: orderId,
                                    googleId,
                                    pageTitle: article?.heading,
                                    pageSlug: article?.slug,
                                    pageSource: article?.source,
                                    requiredAccessLevel:
                                        breachContext?.requiredAccessLevel ||
                                        'subscriber',
                                    masthead: sectionMetaInfo.Title,
                                    callToAction: 'swg-breach',
                                    breachScreenVersion:
                                        breachContext?.breachCreativeName ||
                                        'nav-subscription-button',
                                    pageByline: article.byline || '',
                                    email: userEmail || response.userData.email, // Picks up userEmail & Google Email if using different accounts
                                    rateCode: 'Google_Everyday_Digi',
                                    name: userName || response.userData.name, // Picks up userName & Google Name if using different accounts
                                })
                            } catch (error) {
                                debugSwg(
                                    'Error sending newstart message: %O',
                                    error,
                                )
                            }
                        }

                        // Set the prompt for localStorage to display the pop-up
                        window.localStorage.setItem(
                            'purchase-complete-prompt',
                            'true',
                        )

                        // Internally this creates an account, performs token exchange, and logs in the user
                        const userInfo = await attemptRrmeGoogleAutoLogin({
                            response,
                            sku,
                        })

                        // For RRME, tokens are persisted server-side during token exchange
                        // Redirect directly to desired page if the user is already granted access
                        const redirectUrl = getCteNextUrl(
                            response.userData.email,
                            userInfo,
                            userInfo?.sub,
                        )
                        if (redirectUrl !== null) {
                            debugSwg(`Preparing redirect to ${redirectUrl}...`)
                            window.location.href = redirectUrl
                        } else {
                            debugSwg(
                                `Staying on the same page as user is not granted access yet...`,
                            )
                        }
                    })
                    .catch((error) => {
                        debugSwg('payment promise error', error)
                        throw error
                    })
            })

            debugSwg('subscribing to SKU', sku)
            subscriptions.subscribe(sku)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- isLoggedIn & userEmail changes when the user logs in, and we don't want to re-render the callback!
        [
            localConfig,
            breachInitialised,
            subscriptions,
            dispatch,
            attemptRrmeGoogleAutoLogin,
        ],
    )

    // We want to attach the SwG Button listeners when the following conditions have been fulfilled:
    // 1. SwG.js is loaded (subscriptions)
    // 2. Button is rendered as an element
    // 3. Event Manager in swg.js is initialised
    // 4. The user's authentication status has been confirmed.

    useEffect(() => {
        // Update the current SKU reference
        currentSkuRef.current = googleSKU

        const set =
            subscriptions &&
            buttonRef !== null &&
            eventManagerLoaded &&
            !checkingTokenState &&
            hasAttemptedValidation &&
            breachInitialised

        if (set) {
            debugSwg(`Attaching SwG button handler for SKU: ${googleSKU}`, {
                swgMetaInfo: swgMetaInfo,
            })

            if (!useImprovedSwgButtonHandlers) {
                debugSwg('Using legacy SwG button handler')
                subscriptions.attachButton(buttonRef, () =>
                    handleSubscribeWithGoogleButton(googleSKU, swgMetaInfo),
                )
                return
            }

            debugSwg('Using improved SwG button handlers')
            // Remove previous handler if it exists
            if (lastHandlerRef.current) {
                buttonRef.removeEventListener('click', lastHandlerRef.current)
            }

            // Create the on click handler
            const handler = () =>
                handleSubscribeWithGoogleButton(googleSKU, swgMetaInfo)

            // Add the click event listener
            buttonRef.addEventListener('click', handler)

            // Update the ref to point to the new handler with the current SKU
            lastHandlerRef.current = handler

            // This is required for the SwG button to render correctly
            subscriptions.attachButton(buttonRef, handler)
        }

        // Cleanup event listener
        return () => {
            if (lastHandlerRef.current) {
                buttonRef.removeEventListener('click', lastHandlerRef.current)
                lastHandlerRef.current = null
            }
        }
    }, [
        subscriptions,
        googleSKU,
        handleSubscribeWithGoogleButton,
        eventManagerLoaded,
        checkingTokenState,
        hasAttemptedValidation,
        buttonRef,
        breachInitialised,
        swgMetaInfo,
        useImprovedSwgButtonHandlers,
    ])
}
